/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { CoreErrorKey } from '../key/core-error.key';
import { ApplicationError } from '../model/application-error.model';
import { Change } from '../model/change.model';
import { ChangedEvent } from '../model/changed-event.model';
import {
  ChangeOptions, ObservableCollection, ObservableCollectionEntry, ObservableCollectionOptions
} from './observable-collection.util';

export class ObservableList<T> extends ObservableCollection<number, T> {
  private entryArray = new Array<ObservableCollectionEntry<number, T>>();

  constructor(values?: object, options?: ObservableCollectionOptions) {
    super(options);
    if (values) {
      super.setValues(values, { exclusive: true, force: true, silent: true });
    }
  }

  get size(): number {
    return this.entryArray.length;
  }

  has(index: number): boolean {
    return this.toIndex(index) < this.entryArray.length;
  }

  get(index: number, defaultValue?: T): T {
    index = this.toIndex(index);
    return super.get(index, defaultValue);
  }

  set(index: number, value: T, options?: ChangeOptions): void {
    index = this.toIndex(index);
    super.set(index, value, options);
  }

  insert(index: number, value: T, silent: boolean = false): void {
    index = this.toIndex(index);
    this.internalInsertEntry({ key: index, value: undefined });
    super.set(index, value, { silent: true, force: true });
    if (silent) {
      return;
    }
    const changes: { [key: string]: Change<T> } = {};
    let hasChanges: boolean = false;
    let originalValue = this.entryArray[index + 1] ? this.entryArray[index + 1].value : undefined;
    let currentValue = value;
    if (originalValue !== currentValue) {
      changes[index.toString()] = new Change<T>({ originalValue, currentValue });
      hasChanges = true;
    }
    for (let i = index + 1; i < this.entryArray.length; i++) {
      const originalEntry = this.entryArray[i + 1];
      const currentEntry = this.entryArray[i];
      originalValue = originalEntry ? originalEntry.value : undefined;
      currentValue = currentEntry ? currentEntry.value : undefined;
      if (originalValue !== currentValue) {
        changes[i.toString()] = new Change<T>({ originalValue, currentValue });
        hasChanges = true;
      }
    }
    if (hasChanges) {
      this.fireChangedEvent(new ChangedEvent({ source: this, changes }));
    }
  }

  remove(index: number, silent: boolean = false): boolean {
    index = this.toIndex(index);
    const entry = this.entryArray[index];
    const result = super.remove(index, true);
    if (!result || silent) {
      return result;
    }
    const changes: { [key: string]: Change<T> } = {};
    let hasChanges: boolean = false;
    let originalValue = entry ? entry.value : undefined;
    let currentValue = this.entryArray[index] ? this.entryArray[index].value : undefined;
    if (originalValue !== currentValue) {
      changes[index.toString()] = new Change<T>({ originalValue, currentValue });
      hasChanges = true;
    }
    for (let i = index + 1; i <= this.entryArray.length; i++) {
      const originalEntry = this.entryArray[i - 1];
      const currentEntry = this.entryArray[i];
      originalValue = originalEntry ? originalEntry.value : undefined;
      currentValue = currentEntry ? currentEntry.value : undefined;
      if (originalValue !== currentValue) {
        changes[i.toString()] = new Change<T>({ originalValue, currentValue });
        hasChanges = true;
      }
    }
    if (hasChanges) {
      this.fireChangedEvent(new ChangedEvent({ source: this, changes }));
    }
    return true;
  }

  private toIndex(key: string | number): number {
    if (key === undefined) {
      throw new ApplicationError({
        key: CoreErrorKey.KEY_MISSING,
        message: 'Key missing'
      });
    }
    const index = typeof key === 'number' ? key : parseInt(key, 10);
    if (Number.isNaN(index) || index < 0) {
      throw new ApplicationError({
        key: CoreErrorKey.KEY_INVALID,
        arguments: { key },
        message: 'Key invalid'
      });
    }
    return index;
  }

  push(value: T, silent: boolean = false): void {
    this.insert(this.size, value, silent);
  }

  pop(silent: boolean = false): T {
    if (this.size === 0) {
      return undefined;
    }
    const index = this.size - 1;
    const result = this.get(index);
    this.remove(index, silent);
    return result;
  }

  sort(compare: (a: T, b: T) => number, silent: boolean = false): void {
    if (silent) {
      this.entryArray.sort((a, b) => compare(a ? a.value : undefined, b ? b.value : undefined));
    } else {
      const originalEntryArray: ObservableCollectionEntry<number, T>[] = this.entryArray.slice();
      this.entryArray.sort((a, b) => compare(a ? a.value : undefined, b ? b.value : undefined));
      const changes: { [key: string]: Change<T> } = {};
      let hasChanges: boolean = false;
      for (let index = 0; index < this.entryArray.length; index++) {
        const originalValue = originalEntryArray[index] ? originalEntryArray[index].value : undefined;
        const currentValue = this.entryArray[index] ? this.entryArray[index].value : undefined;
        if (originalValue !== currentValue) {
          changes[index.toString()] = new Change({ originalValue, currentValue });
          hasChanges = true;
        }
      }
      if (hasChanges) {
        this.fireChangedEvent(new ChangedEvent({ source: this, changes }));
      }
    }
  }

  setValues(values: object, options?: ChangeOptions): { [key: string]: Change<T> } {
    const exclusive = options && options.exclusive || false;
    if (!exclusive) {
      return super.setValues(values, options);
    }
    const silent = options && options.silent || false;
    let lastIndex: number = -1;
    if (values) {
      Object.keys(values).forEach((key: string | number) => {
        if (values[key] === undefined) {
          return;
        }
        if (typeof key !== 'number') {
          key = parseInt(key, 10);
        }
        if (key > lastIndex) {
          lastIndex = key;
        }
      });
    }
    const changes = super.setValues(values, Object.assign({}, options, { silent: true }));
    this.entryArray.length = lastIndex + 1;
    if (changes && !silent) {
      this.fireChangedEvent(new ChangedEvent({ source: this, changes }));
    }
  }

  indexOf(value: T): number {
    const key = this.keyOf(value);
    return key !== undefined ? key : -1;
  }

  firstIndex(predicate: (value: T, index?: number) => boolean = (() => true)): number {
    const firstKey = this.firstKey(predicate);
    return firstKey !== undefined ? firstKey : -1;
  }

  lastIndex(predicate: (value: T, index?: number) => boolean = (() => true)): number {
    const lastKey = this.lastKey(predicate);
    return lastKey !== undefined ? lastKey : -1;
  }

  lastKey(predicate: (value: T, index?: number) => boolean = (() => true)): number {
    const lastEntry = this.lastEntry(predicate);
    return lastEntry && lastEntry[0];
  }

  lastValue(predicate: (value: T, index?: number) => boolean = (() => true)): T {
    const lastEntry = this.lastEntry(predicate);
    return lastEntry && lastEntry[1];
  }

  lastEntry(predicate: (value: T, index?: number) => boolean = (() => true)): [number, T] {
    for (let i = this.entryArray.length - 1; i >= 0; i--) {
      const entry = this.entryArray[i];
      if (predicate(entry.value, i)) {
        return [entry.key, entry.value];
      }
    }
    return undefined;
  }

  toJSON(): Array<T> {
    return this.entryArray.map((entry: ObservableCollectionEntry<number, T>) => {
      if (!entry) {
        return undefined;
      }
      const value: any = entry.value;
      return (value && typeof value.toJSON === 'function') ? value.toJSON() : value as T;
    });
  }

  protected internalCreateEntry(key: number, value: T): ObservableCollectionEntry<number, T> {
    if (typeof key !== 'number') {
      key = parseInt(key, 10);
    }
    return super.internalCreateEntry(key, value);
  }

  protected internalHasEntry(index: number) {
    return this.entryArray[index] !== undefined;
  }

  protected internalGetEntry(index: number) {
    return this.entryArray[index];
  }

  protected internalSetEntry(entry: ObservableCollectionEntry<number, T>): void {
    if (entry.value === undefined) {
      this.entryArray[entry.key] = undefined;
    } else {
      this.entryArray[entry.key] = entry;
    }
  }

  protected internalInsertEntry(entry: ObservableCollectionEntry<number, T>): void {
    this.entryArray.splice(entry.key, 0, entry);
    for (let i = entry.key + 1; i < this.entryArray.length; i++) {
      if (this.entryArray[i]) {
        this.entryArray[i].key++;
      }
    }
  }

  protected internalRemoveEntry(index: number): boolean {
    if (index < 0 || index >= this.entryArray.length) {
      return false;
    }
    this.entryArray.splice(index, 1);
    for (let i = index; i < this.entryArray.length; i++) {
      if (this.entryArray[i]) {
        this.entryArray[i].key--;
      }
    }
    return true;
  }

  protected internalEntries(): IterableIterator<ObservableCollectionEntry<number, T>> {
    return this.entryArray.values();
  }

}
